import { StatusSolicitacao } from '../services/solicitacoes.service';

/**
 * Os contratos da **solicitação de serviço** (Carta de Serviços) e os tipos de tela do fluxo.
 *
 * Os `*Response` espelham o JSON do back-end (`api/sai/cartaservico/publico/...`) e só o
 * service os conhece. O resto do fluxo manipula os tipos de tela abaixo.
 */

/** Instrução de renderização do campo — decide qual controle a etapa desenha. */
export type TipoCampoSolicitacao =
  | 'Texto Curto'
  | 'Texto Longo'
  | 'Número'
  | 'Data'
  | 'Seleção'
  | 'Upload de Arquivo';

export interface CampoFormularioPublicoResponse {
  /** A chave do campo NESTE momento — o adm a troca a cada gravação do serviço. */
  codCampoServicoCps: number;
  desNomeCps: string;
  desTipoCps: TipoCampoSolicitacao;
  bitObrigatorioCps: boolean;
  /** Alternativas do tipo 'Seleção'; vazio nos outros. */
  opcoes: readonly string[];
  numOrdemCps: number;
}

export interface RegrasAnexoPublicoResponse {
  bitPermitirAnexosSrv: boolean;
  bitAnexoObrigatorioSrv: boolean;
  numQuantidadeMaximaSrv: number;
  /** Rótulo do adm ('10 MB'), para exibir. */
  desTamanhoMaximoSrv: string | null;
  /** O mesmo teto em bytes, para comparar com o arquivo. */
  numTamanhoMaximoBytes: number;
  desOrientacoesAnexoSrv: string | null;
  /** 'PDF', 'JPG', 'PNG', 'DOC', 'DOCX'. */
  formatos: readonly string[];
}

export interface LocalAtendimentoPublicoResponse {
  desNomeOrg: string | null;
  desEndereco: string | null;
}

/** `GET publico/servico/{id}/formulario` — o formulário da solicitação de um serviço. */
export interface FormularioServicoPublicoResponse {
  codServicoSrv: number;
  desNomeSrv: string;
  bitPermitirAnonimoSrv: boolean;
  bitExigirGovBrSrv: boolean;
  bitAceitaSolicitacao: boolean;
  bitExigeAgendamento: boolean;
  bitAgendaDisponivel: boolean;
  /** Se o wizard oferece "Segunda via" no tipo do pedido. O prazo não vem: o portal não o mostra. */
  bitPermiteSegundaViaSrv: boolean;
  campos: readonly CampoFormularioPublicoResponse[];
  anexos: RegrasAnexoPublicoResponse;
  localAtendimento: LocalAtendimentoPublicoResponse | null;
}

export interface DiaAgendaPublicoResponse {
  /** `aaaa-mm-dd`. */
  data: string;
  /** `HH:mm`, em ordem. */
  horarios: readonly string[];
}

/** `GET publico/servico/{id}/agenda?mes=aaaa-mm` — só dias com horário livre. */
export interface AgendaPublicaResponse {
  mes: string;
  dataMinima: string;
  dataMaxima: string;
  dias: readonly DiaAgendaPublicoResponse[];
}

/** O 201 de `POST publico/solicitacao`. */
export interface SolicitacaoAberturaResponse {
  desProtocoloSsv: string;
  desStatusSsv: string;
  /** Data de abertura, ISO sem fuso. */
  datInicioSsv: string;
  desNomeServicoSsv: string;
  bitAnonimaSsv: boolean;
  /** O nome de quem pediu, na identificada; nulo na anônima. Só a abertura o devolve. */
  desNomeSolicitanteSsv: string | null;
  datAgendamentoSsv: string | null;
  desLocalAtendimentoSsv: string | null;
  numAnexos: number;
}

/**
 * Um anexo da resposta do órgão. Só código, nome e tamanho: o arquivo sai por
 * `GET publico/solicitacao/{protocolo}/resposta/anexo/{codAnexoRespostaSolicitacaoArs}`.
 */
export interface AnexoRespostaPublicaResponse {
  codAnexoRespostaSolicitacaoArs: number;
  desNomeOriginalArs: string;
  numTamanhoBytesArs: number;
}

/**
 * Uma resposta do órgão na consulta pública. **Sem autor**, por desenho: quem tem o protocolo
 * vê o que o órgão respondeu, não o nome do servidor que respondeu.
 */
export interface RespostaPublicaResponse {
  /** Data da resposta, ISO sem fuso. */
  datInicioRss: string;
  /** O status em que a solicitação ficou com esta resposta ('Respondida', 'Aguardando complemento'...). */
  desStatusRss: string;
  desTextoRss: string;
  /** URLs, na ordem em que o órgão as informou. */
  links: readonly string[];
  /** Os nomes dos formulários indicados, na ordem em que o órgão os informou. */
  formularios: readonly string[];
  anexos: readonly AnexoRespostaPublicaResponse[];
}

/**
 * O complemento que o órgão pediu e ainda espera. Sem o autor: o nome do servidor não sai
 * para quem tem o protocolo.
 */
export interface PendenciaPublicaResponse {
  desMensagemPds: string;
  datInicioPds: string;
  /** Até quando enviar; nulo quando o pedido não pausa o prazo do órgão. */
  datLimitePds: string | null;
  bitPausaPrazoPds: boolean;
}

/** `GET publico/solicitacao/{protocolo}` — o protocolo é a credencial. */
export interface SolicitacaoPublicaResponse {
  desProtocoloSsv: string;
  desNomeServicoSsv: string;
  desStatusSsv: string;
  datInicioSsv: string;
  /** Nula no agendamento feito pela área do cidadão, que não tem formulário. */
  desDescricaoSsv: string | null;
  /** Nulo pelo mesmo motivo. */
  desEndereco: string | null;
  datAgendamentoSsv: string | null;
  desLocalAtendimentoSsv: string | null;
  bitSegundaViaSsv: boolean;
  /** 'DiasCorridos', 'DiasUteis', 'Horas', 'Imediato' ou nulo (sem prazo). Diz se a previsão leva hora. */
  desTipoContagemSsv: string | null;
  /** A previsão de conclusão; nula sem prazo. */
  datLimiteResposta: string | null;
  bitPrazoPausado: boolean;
  pendenciaAberta: PendenciaPublicaResponse | null;
  anexos: readonly { desNomeOriginalAns: string; numTamanhoBytesAns: number }[];
  historico: readonly { datEventoEvs: string; desStatusNovoEvs: string; desMensagemEvs: string }[];
  /** As respostas do órgão, da mais **antiga** para a mais recente — quem inverte é a tela. */
  respostas: readonly RespostaPublicaResponse[];
}

/** As etapas do fluxo por identidade, não por posição. */
export type EtapaSolicitacaoId = 'informacoes' | 'solicitacao' | 'anexos' | 'confirmacao';

export interface EtapaSolicitacao {
  id: EtapaSolicitacaoId;
  rotulo: string;
}

export const ETAPAS_SOLICITACAO: readonly EtapaSolicitacao[] = [
  { id: 'informacoes', rotulo: 'Informações' },
  { id: 'solicitacao', rotulo: 'Solicitação' },
  { id: 'anexos', rotulo: 'Anexos' },
  { id: 'confirmacao', rotulo: 'Confirmação' },
];

/** Como a pessoa entrou no fluxo. Só o anônimo envia nesta versão. */
export type ModoAcessoSolicitacao = '' | 'anonimo' | 'identificado';

/** A escolha feita no calendário. */
export interface AgendamentoEscolhido {
  /** `aaaa-mm-dd`. */
  data: string;
  /** `HH:mm`. */
  horario: string;
}

/** O que a tela decide a partir de um erro da API. */
export type TipoErroSolicitacao =
  | 'validacao'
  | 'identificacao'
  | 'sessao'
  | 'nao-encontrado'
  | 'vaga'
  | 'formulario'
  | 'limite'
  | 'indisponivel'
  | 'desafio'
  | 'captcha';

export interface ErroSolicitacaoServico {
  tipo: TipoErroSolicitacao;
  mensagem: string;
  detalhes: readonly string[];
}

/** O prefixo do protocolo da Carta de Serviços — é por ele que o acompanhamento decide a quem perguntar. */
export const PREFIXO_PROTOCOLO_SERVICO = 'CSE-';

/** Na contagem em horas a previsão leva a hora; nas de dia, só a data. */
export const TIPO_CONTAGEM_HORAS = 'Horas';

/** Os limites do complemento — os mesmos da resposta do órgão, e não os do serviço. */
export const TAMANHO_MAXIMO_COMPLEMENTO = 4000;
export const LIMITE_ANEXOS_COMPLEMENTO = 5;
export const TETO_ANEXO_COMPLEMENTO_BYTES = 10 * 1024 * 1024;
export const FORMATOS_ANEXO_COMPLEMENTO: readonly string[] = ['PDF', 'JPG', 'PNG', 'DOC', 'DOCX'];

/** Os status em que o prazo do órgão já parou de contar — os mesmos `StatusEncerrados` do back-end. */
const STATUS_SEM_PREVISAO: readonly string[] = ['Respondida', 'Concluída', 'Arquivada', 'Cancelada'];

/** Os campos que decidem a linha "Prazo" — os mesmos na consulta pública e em "Minhas solicitações". */
export type PrazoDaSolicitacao = Pick<
  SolicitacaoPublicaResponse,
  'desStatusSsv' | 'bitPrazoPausado' | 'datLimiteResposta' | 'desTipoContagemSsv'
>;

/**
 * A linha "Prazo" do acompanhamento e de "Minhas solicitações". Pausado vence a data, porque a
 * data ainda vai andar; sem prazo ou já encerrada, vazio — e a tela omite a linha.
 */
export function textoPrazoSolicitacao(solicitacao: PrazoDaSolicitacao): string {
  if (STATUS_SEM_PREVISAO.includes(solicitacao.desStatusSsv)) {
    return '';
  }

  if (solicitacao.bitPrazoPausado) {
    return 'Prazo pausado enquanto aguardamos o seu complemento';
  }

  if (!solicitacao.datLimiteResposta) {
    return '';
  }

  return `Previsão de conclusão: até ${formatarDataPrazo(solicitacao.datLimiteResposta, solicitacao.desTipoContagemSsv)}`;
}

/** `dd/mm/aaaa`, ou `dd/mm/aaaa às HH:mm` na contagem em horas. Lê a data ISO sem fuso como local. */
export function formatarDataPrazo(dataIso: string, tipoContagem: string | null): string {
  const [data, hora = ''] = dataIso.split('T');
  const [ano, mes, dia] = data.split('-');
  const dataTexto = `${dia}/${mes}/${ano}`;

  return tipoContagem === TIPO_CONTAGEM_HORAS && hora ? `${dataTexto} às ${hora.slice(0, 5)}` : dataTexto;
}

/** Os tipos de atendimento que levam ao fluxo de solicitação (mesma regra do back-end). */
const TIPOS_QUE_ACEITAM_SOLICITACAO: readonly string[] = ['Solicitação Online', 'Agendamento'];

export function aceitaSolicitacao(tiposAtendimento: readonly string[]): boolean {
  return tiposAtendimento.some((tipo) => TIPOS_QUE_ACEITAM_SOLICITACAO.includes(tipo));
}

/** As 27 unidades federativas — o mesmo conjunto que o back-end aceita. */
export const UFS: readonly string[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

/**
 * O status da solicitação de serviço (vocabulário do CDS-1, no domínio do back-end) para o
 * status da tela de acompanhamento, que agrupa ouvidoria, SIC e serviço.
 */
export function statusParaAcompanhamento(status: string): StatusSolicitacao {
  switch (status) {
    case 'Recebida':
      return 'aberto';
    case 'Respondida':
      return 'respondido';
    case 'Concluída':
    case 'Arquivada':
      return 'finalizado';
    case 'Cancelada':
      // O agendamento, cancelado pelo cidadão em "Meus agendamentos" ou pelo órgão, e a
      // solicitação sem agendamento, cancelada pelo cidadão em "Minhas solicitações".
      return 'cancelado';
    default:
      // Em triagem, Em análise, Encaminhada, Aguardando complemento, Reaberta.
      return 'em-andamento';
  }
}
